import { DateTime } from 'luxon'
import { isEmpty } from 'lodash'
import { PlanType } from '@labrav/react-components'
import { excludedProductTypes } from '@labrav/common'
import {
  marketingCampaignEndDate,
  marketingCampaignStartDate,
} from '../../common/utils/constants'
import {
  CloudMarketplace,
  FlyOutAWSProductListingType,
  FreeTrialStatus,
  Product,
  ProductStatus,
  allowedToDeleteProductSelection,
  allowedToPublishProductSelection,
  allowedToUnpublishProductSelection,
  notAllowedProductSelection,
} from '../../modules/productsListing/types'
import { FlyOutListingUserType } from '../../types/external'
import { FlyoutListingProductType } from '../../types/addEditProduct'
import { ExternalPlatformType, type GetMenuOptionsArgs } from './types'

export const mapping = {
  'Product title': 'sellerProductName',
  'Product type': 'type',
  Status: 'status',
  Subscriptions: 'noOfActiveContracts',
  '': 'action',
}

export const mappingAdminUi = {
  'Product title': 'sellerProductName',
  'Product ID': 'sellerProductId',
  Status: 'status',
  Contracts: 'noOfContracts',
  Actions: 'actions',
}

export const impartnerMapping = {
  'Product title': 'sellerProductName',
  'Product type': 'type',
  Status: 'status',
  '': 'action',
}

export const getFreeTrialTextWithDisable = (status: FreeTrialStatus) => {
  switch (status) {
    case FreeTrialStatus.FREE_TRIAL_CREATION_IN_PROGRESS:
      return {
        disabled: true,
        label: 'View free trial',
        tooltip: 'Free trial creation In Progress',
      }
    case FreeTrialStatus.FREE_TRIAL_CREATED:
      return { disabled: false, label: 'View free trial', isView: true }
    case FreeTrialStatus.FREE_TRIAL_DEACTIVATION_IN_PROGRESS:
      return {
        disabled: true,
        label: 'View free trial',
        tooltip: 'Free trial deactivation in progress',
      }
    case FreeTrialStatus.FREE_TRIAL_DEACTIVATED:
      return { disabled: false, label: 'Create free trial' }
    default:
      return { disabled: false, label: 'Create free trial' }
  }
}

export const getPubishTextWithDisable = (status: ProductStatus) => {
  switch (status) {
    case ProductStatus.PRODUCT_STATUS_RESTRICTED:
      return {
        disabled: true,
        label: 'Publish',
        tooltip:
          'please create a support ticket with Labra to publish restricted listing',
      }
    default:
      return {
        disabled: false,
        label:
          'please create a support ticket with Labra to publish restricted listing',
      }
  }
}

export const getFreeTrialTextForProduct = (productRow: Product) => {
  switch (productRow?.productType) {
    case PlanType.SAAS_CONTRACT: {
      return {
        ...getFreeTrialTextWithDisable(
          productRow?.freeTrial?.freeTrialStatus || ('' as FreeTrialStatus)
        ),
      }
    }
    default: {
      return {
        disabled: true,
        label: 'Create free trial',
        tooltip: 'Free trial cannot be created for this product type',
      }
    }
  }
}

export const getPublishTextForProduct = (productRow: Product) => {
  switch (productRow?.productType) {
    case PlanType.SAAS_CONTRACT: {
      return {
        ...getPubishTextWithDisable(productRow?.status),
      }
    }
    default: {
      return {
        disabled: true,
        label: 'Publish',
        tooltip:
          'please create a support ticket with Labra to publish restricted listing',
      }
    }
  }
}

export const isProductMeteringDisabled = (
  productRow: Product,
  cloudProvider: CloudMarketplace
) => {
  if (cloudProvider !== 'AWS') return false

  if (!excludedProductTypes.includes(productRow.productType as PlanType)) {
    return true
  }

  const isAdditionalUsage = productRow?.productDimensions?.some(
    ({ isAdditionalUsageAllowed }) => isAdditionalUsageAllowed
  )

  if (
    productRow?.productType === PlanType.SAAS_CONTRACT &&
    !isAdditionalUsage
  ) {
    return true
  }

  return false
}

export const getEditViewProductRedirectUrl = (
  productRow: Product,
  cloud?: string,
  accountId?: string
) => {
  return productRow.status === ProductStatus.PRODUCT_STATUS_DRAFT ||
    (productRow.status === ProductStatus.PRODUCT_STATUS_CREATION_FAILED &&
      isEmpty(productRow.awsProductId))
    ? `./${productRow.productId}/edit?listingTypeInfo=${FlyoutListingProductType.NEW}&awsProductType=${productRow?.isAgenticProduct ? FlyOutAWSProductListingType.AGENTIC_AI : productRow?.productType}`
    : `./${productRow.productId}/details`
}

export const getMenuOptions = ({
  productRow,
  cloudProvider,
  externalBaseURL,
  search,
  externalPlatformType,
  cloud,
  accountId,
}: GetMenuOptionsArgs) => {
  const freeTrialTextAndLabel = getFreeTrialTextForProduct(productRow)
  const publishTextAndLabel = getPublishTextForProduct(productRow)
  const isHubspot = externalPlatformType === ExternalPlatformType.HUBSPOT
  const hideMeteringOption =
    !!externalBaseURL ||
    isHubspot ||
    !productRow?.numberOfActiveContracts ||
    productRow?.status === ProductStatus.PRODUCT_STATUS_CREATION_FAILED

  const isDisableMetering = isProductMeteringDisabled(productRow, cloudProvider)
  const disableMeteringTooltip = isDisableMetering ? 'Cannot be metered' : ''

  const cloudPath = cloud?.toLowerCase() || cloudProvider.toLowerCase()
  const baseUrl = accountId
    ? `/flyout/${cloudPath}/${accountId}/products-listing`
    : `/flyout/products-listing`

  const azureDisable = [
    ProductStatus.PRODUCT_STATUS_MIGRATION_IN_QUEUE,
    ProductStatus.MIGRATION_IN_PROGRESS,
    ProductStatus.MIGRATION_FAILED,
  ].includes(productRow?.status)
  const offersBaseUrl = accountId
    ? `/flyout/${cloudPath}/${accountId}`
    : `/flyout`

  const options = [
    {
      key: 'create-private-offer',
      label: 'Create private offer',
      route: `${externalBaseURL || baseUrl}/${
        productRow.productId
      }/private-offers/edit${search ? search : ''}`,
      disable: azureDisable,
      disabledTooltip:
        productRow?.status === ProductStatus.PRODUCT_STATUS_MIGRATION_IN_QUEUE
          ? 'Resume your migration from the top banner.'
          : '',
    },
    {
      key: 'view-private-offers',
      label: 'View private offers',
      route: `${
        externalBaseURL || offersBaseUrl
      }/offers-authorizations/private-offers${search ? search : ''}`,
      disable: azureDisable,
      disabledTooltip:
        productRow?.status === ProductStatus.PRODUCT_STATUS_MIGRATION_IN_QUEUE
          ? 'Resume your migration from the top banner.'
          : '',
    },
    ...(!hideMeteringOption
      ? [
          {
            key: 'create-metering',
            label: 'Create metering',
            disable: isDisableMetering,
            disabledTooltip: disableMeteringTooltip,
            route: `${baseUrl}/${productRow.productId}/contracts`,
          },
        ]
      : []),
    {
      key: 'view-revenue',
      label: 'View revenue',
      route: accountId
        ? `/flyout/${cloud}/${accountId}/revenue`
        : '/flyout/revenue',
    },
    {
      key: 'view-free-trial',
      label: freeTrialTextAndLabel.label,
      disable: freeTrialTextAndLabel.disabled,
      disabledTooltip: freeTrialTextAndLabel.tooltip,
      params: {
        productId: productRow.productId,
        isView: freeTrialTextAndLabel?.isView,
      },
    },
    {
      key: 'delete-product',
      label: 'Delete',
      params: {
        productId: productRow.productId,
        productName: productRow.productName,
        productStatus: productRow.status,
      },
    },
    {
      key: 'publish-product',
      label: 'Publish',
      disable: publishTextAndLabel.disabled,
      disabledTooltip: publishTextAndLabel.tooltip,
      params: {
        productId: productRow.productId,
        productName: productRow.productName,
        productStatus: productRow.status,
      },
    },
    {
      key: 'unpublish-product',
      label: 'Unpublish',
      params: {
        productId: productRow.productId,
        productName: productRow.productName,
        productStatus: productRow.status,
      },
    },
  ]
  if (
    productRow.productType &&
    [PlanType.SAAS_CONTRACT, PlanType.SAAS_SUBSCRIPTION].includes(
      productRow.productType
    )
  ) {
    options.push({
      key: 'clone-product',
      label: 'Clone listing',
      disable:
        productRow.status === ProductStatus.PRODUCT_STATUS_IN_PROGRESS
          ? true
          : false,
      disabledTooltip: 'Cloning not available',
      route: `./edit?cloneProductId=${
        productRow.productId
      }&cloneProductName=${productRow?.productName}
      &isDraftListing=${String(
        productRow.status === ProductStatus.PRODUCT_STATUS_DRAFT
      )}&listingTypeInfo=${FlyOutListingUserType.NEW}&awsProductType=${
        productRow?.isAgenticProduct
          ? FlyOutAWSProductListingType.AGENTIC_AI
          : productRow?.productType
      }`,
    })
  }
  switch (cloudProvider) {
    case 'AWS':
      if (externalBaseURL || isHubspot) {
        return options.slice(0, 2)
      } else {
        const shouldHideOfferAndFreeTrialRoutes =
          productRow.status &&
          notAllowedProductSelection.includes(productRow.status)
        const filteredOptions = options.filter(
          option =>
            option.key !== 'create-private-offer' &&
            option.key !== 'view-free-trial'
        )
        let updatedOptions = shouldHideOfferAndFreeTrialRoutes
          ? filteredOptions
          : options
        const redirectURL = getEditViewProductRedirectUrl(
          productRow,
          cloud,
          accountId
        )
        let editOption = [
          {
            key: 'edit-draft-product',
            label: 'Edit listing',
            route: redirectURL,
          },
          {
            key: 'delete-product',
            label: 'Delete product',
            params: {
              productId: productRow.productId,
              productName: productRow.productName,
              productStatus: productRow.status,
            },
          },
        ]

        const shouldShowDeleteOption =
          productRow.status &&
          allowedToDeleteProductSelection.includes(productRow.status)

        if (!shouldShowDeleteOption) {
          updatedOptions = updatedOptions.filter(
            option => option.key !== 'delete-product'
          )

          const shouldShowPublishOption =
            productRow.status &&
            allowedToPublishProductSelection.includes(productRow.status)

          if (!shouldShowPublishOption) {
            updatedOptions = updatedOptions.filter(
              option => option.key !== 'publish-product'
            )
          }
          const shouldShowUnpublishOption =
            productRow.status &&
            allowedToUnpublishProductSelection.includes(productRow.status)

          if (!shouldShowUnpublishOption) {
            updatedOptions = updatedOptions.filter(
              option => option.key !== 'unpublish-product'
            )
          }
          editOption = editOption.filter(
            option => option.key !== 'delete-product'
          )
        }

        const isDraftEditable =
          productRow.productType &&
          [PlanType.SAAS_CONTRACT, PlanType.SAAS_SUBSCRIPTION].includes(
            productRow.productType
          )
        const cloneOption = updatedOptions.filter(
          itm => itm?.key === 'clone-product'
        )
        const isInProgress =
          productRow.status === ProductStatus.PRODUCT_STATUS_IN_PROGRESS
        return [
          ...(isDraftEditable && !isInProgress ? editOption : []),
          ...(!isInProgress &&
          (productRow.status === ProductStatus.PRODUCT_STATUS_DRAFT ||
            productRow.status === ProductStatus.PRODUCT_STATUS_CREATION_FAILED)
            ? cloneOption
            : []),
          ...(productRow.status !==
            ProductStatus.PRODUCT_STATUS_CREATION_FAILED &&
          productRow.status !== ProductStatus.PRODUCT_STATUS_DRAFT
            ? updatedOptions
            : []),
        ]
      }
    case 'AZURE':
    case 'GCP':
      return options.slice(0, 2)
    default:
      return options
  }
}

export const getProductListingCloudChangePath = ({
  cloud,
  originalAccountId,
  isHubspot,
  isImpartner,
  secureKeyFromURL,
  partnerIdFromURL,
  search,
}: {
  cloud: string
  originalAccountId: string
  isHubspot: boolean
  isImpartner: boolean
  secureKeyFromURL?: string
  partnerIdFromURL?: string
  search?: string
}) => {
  const cloudPath = cloud.toLowerCase()

  if (isHubspot && secureKeyFromURL && partnerIdFromURL) {
    const searchPart = search
      ? search.startsWith('?')
        ? search
        : `?${search}`
      : ''
    return `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerIdFromURL}/cloud/${cloudPath}/accounts/${originalAccountId}/product-listing${searchPart}`
  }

  if (isImpartner) {
    return secureKeyFromURL
      ? `/external-apps/${secureKeyFromURL}/partners/${partnerIdFromURL}/flyout/${cloudPath}/${originalAccountId}/products-listing`
      : `/external-apps/partners/${partnerIdFromURL}/flyout/${cloudPath}/${originalAccountId}/products-listing`
  }

  return `/flyout/${cloudPath}/${originalAccountId}/products-listing`
}

export const isMarketingCampaignActive = () => {
  const currentDate = DateTime.utc()
  const campaignStartDate = DateTime.fromFormat(
    marketingCampaignStartDate,
    'yyyy-MM-dd'
  ).toUTC()
  const campaignEndDate = DateTime.fromFormat(
    marketingCampaignEndDate,
    'yyyy-MM-dd'
  ).toUTC()
  return currentDate >= campaignStartDate && currentDate <= campaignEndDate
}

export const getListingMutationAlerts = ({
  productName,
  productStatus,
  action,
}: {
  productName?: string
  productStatus?: string
  action: 'deleted' | 'published' | 'unpublished'
}) => ({
  success: `"${productName}" ${productStatus} listing ${action} succesfully`,
  error: `"${productName}" ${productStatus} listing could not be ${action}`,
})

export const getProductListingTableMapping = ({
  isAdmin,
  isImpartner,
}: {
  isAdmin: boolean
  isImpartner: boolean
}) => {
  if (isAdmin) return mappingAdminUi
  if (isImpartner) return impartnerMapping
  return mapping
}
